import { Arrow } from "konva/lib/shapes/Arrow";
import { nodeSize, scaleSize } from "../shapes";
import { WhiteboardLink } from "./link";
import { BasicShape } from "../shapes/basic-shape";
import { WhiteboardLinkStateType } from ".";

export type ShapeType = "rectangle" | "ellipse" | "rounded-rectangle" | "rhombus" | "parallelogram";
export type ConnectionType = "solid" | "dotted";
export type ArrowType = "left-sided" | "right-sided" | "two-sided" | "no-pointer";
export type Orientation = "top" | "bottom" | "left" | "right";

export interface IBasicLinkSource {
  type: ShapeType;
  id: string;
  orientation?: Orientation;
}

export interface IBasicLinkTarget {
  type: ShapeType;
  id: string;
  orientation?: string;
}

export interface IBasicLinkState {
  type: "connection";
  source: IBasicLinkSource;
  target: IBasicLinkTarget;
  color?: string;
  connectionType?: ConnectionType;
  arrowType?: ArrowType;
}

const eventName = (link: IBasicLinkState): string => `.base-link-${link.source.id}-${link.target.id}`;

export class BasicLink extends WhiteboardLink<IBasicLinkState> {
  private source: BasicShape;
  private target: BasicShape;

  static stateTypes: WhiteboardLinkStateType[] = ["connection"];

  constructor(link: IBasicLinkState) {
    super(link);

    this.arrow();
  }

  connectShapes(source: BasicShape, target: BasicShape): void {
    this.source = source;
    this.target = target;

    this.updatePoints();

    const updatePoints = () => this.updatePoints();
    (this.source as BasicShape).on(`dragmove.${eventName(this.link)} resize.${eventName(this.link)}`, updatePoints);
    (this.target as BasicShape).on(`dragmove.${eventName(this.link)} resize.${eventName(this.link)}`, updatePoints);
  }

  private arrow() {
    this.add(
      new Arrow({
        name: "arrow",
        fill: "#81ACFF",
        stroke: "#81ACFF",
        strokeWidth: 2,
        bezier: true,
        points: [],
      })
    );
  }

  private updatePoints() {
    const stage = this.source.getStage();
    const stagePos = stage.position();
    const scale = stage.scale();

    // start from the middle left side of the source shape
    const sourceSize = scaleSize(scale, nodeSize(this.source));
    const sourceAbsPos = this.source.absolutePosition();
    const sourcePos = {
      x: (-1 * stagePos.x + sourceAbsPos.x) / scale.x - 10,
      y: (-1 * stagePos.y + sourceAbsPos.y) / scale.y + sourceSize.height / 2,
    };

    // finish at the middle right side of the target shape
    const targetAbs = this.target.absolutePosition();
    const targetSize = nodeSize(this.target);

    const targetAbsY = targetAbs.y;
    const targetHeight = targetSize.height;

    const targetPos = {
      x: (-1 * stagePos.x + targetAbs.x) / scale.x + targetSize.width + 10,
      y: (-1 * stagePos.y + targetAbsY) / scale.y + targetHeight / 2,
    };

    // 4-point bezier curve
    const x0 = sourcePos.x;
    const y0 = sourcePos.y;
    const x1 = (sourcePos.x + targetPos.x) / 2;
    const y1 = y0;
    const x2 = x1;
    const y2 = targetPos.y;
    const x3 = targetPos.x;
    const y4 = y2;
    this.findOne<Arrow>(".arrow").points([x0, y0, x1, y1, x2, y2, x3, y4]);
  }
}
